import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Avatar,
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { header } from '../components/header.encry';
import StoresHelper from '../components/storesHelper';
import { generateBarcodeFromValue } from '../components/barcodeHelper';
import { formatOrderTicketDateTime } from '../utils/orderTicket';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchUsers } from '../components/UserService';
import { updateDolibarrOrderUser } from '../components/DolibarrOrderService';
import ProductService from '../components/ProductService';
import VariableService from '../components/VariableService';
import { toast } from 'react-toastify';
import {
  buildCarrefourPluTicket,
  resolveStoredPlu,
} from '../utils/carrefourPlu';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ALT_DATE_REGEX = /^\d{2}-\d{2}-\d{4}$/;

const parseDateInput = value => {
  if (!value) return null;
  if (ISO_DATE_REGEX.test(value)) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (ALT_DATE_REGEX.test(value)) {
    const [first, second, year] = value.split('-');
    const parsed = new Date(`${year}-${first}-${second}`);
    if (!Number.isNaN(parsed.getTime())) return parsed;
    const swapped = new Date(`${year}-${second}-${first}`);
    return Number.isNaN(swapped.getTime()) ? null : swapped;
  }
  return null;
};

const toIsoDate = dateObj => dateObj.toISOString().slice(0, 10);

const formatDateLabel = (startDate, endDate) => {
  if (!startDate) return '';
  const toPairs = dateObj => {
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const year = dateObj.getFullYear();
    return {
      us: `${month}/${day}/${year}`,
      eu: `${day}/${month}/${year}`
    };
  };
  const start = toPairs(startDate);
  if (endDate) {
    const end = toPairs(endDate);
    return `${start.eu} (${start.us}) au ${end.eu} (${end.us})`;
  }
  return `${start.eu} (${start.us})`;
};

const MAX_RANGE_DAYS_RESTRICTED = 6; // inclusive range of 7 days max

const parseRangeValues = (startValue, endValue, maxRangeDays) => {
  const startDate = parseDateInput(startValue);
  const endDate = parseDateInput(endValue);
  if (!startDate || !endDate) {
    return { valid: false };
  }
  if (startDate > endDate) {
    return { valid: false, reason: 'order' };
  }
  if (typeof maxRangeDays === 'number') {
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays > maxRangeDays) {
      return { valid: false, reason: 'rangeLimit', startDate, endDate, maxRangeDays };
    }
  }
  return { valid: true, startDate, endDate };
};

const resolveOrdersEndpoint = (startIso, endIso) => {
  if (!startIso) return 'today';
  if (startIso === 'today' && !endIso) return 'today';
  if (endIso && endIso !== startIso) {
    return `range/${encodeURIComponent(startIso)}/${encodeURIComponent(endIso)}`;
  }
  return encodeURIComponent(startIso);
};

const parseNumeric = value => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeId = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.$oid) return value.$oid;
  return String(value);
};

const addProductLookupKey = (map, value, product) => {
  const normalized = normalizeId(value);
  if (normalized) {
    map[normalized] = product;
  }
};

const resolveImageUrl = product => {
  const rawImage =
    product?.url_product || product?.urlImage || product?.imageUrl || product?.picture || product?.image;
  if (!rawImage) return '';
  if (String(rawImage).startsWith('http')) {
    return rawImage;
  }
  const normalized = String(rawImage).replace(/^\//, '');
  return `${process.env.REACT_APP_DATA_PIC_PATH}/${normalized}`;
};

const normalizeOrderLines = order => {
  const referenceProducts = Array.isArray(order?.reference_products)
    ? order.reference_products
    : [];
  const soldProducts = Array.isArray(order?.products) ? order.products : [];
  const source = soldProducts.length ? soldProducts : referenceProducts;

  return source.map(item => {
    const title =
      item?.fullproduct?.name ||
      item?.fullproduct?.label ||
      item?.title ||
      item?.name ||
      item?.product_label ||
      'Produit';

    const quantityRaw = item?.qty ?? item?.quantity ?? 0;
    const quantityParsed = parseInt(quantityRaw, 10);
    const quantity = Number.isFinite(quantityParsed) && quantityParsed > 0 ? quantityParsed : 1;

    const unitPrice = parseNumeric(
      item?.fullproduct?.regular_price ??
        item?.price ??
        item?.fullproduct?.price ??
        item?.fullproduct?.prices?.price ??
        0
    );

    const barcode =
      item?.fullproduct?.barcode || item?.barcode || item?.fullproduct?.dolibarr_barcode || '';

    return {
      productId: normalizeId(item?.product_id || item?.id_product || item?.fk_product || item?.fullproduct?._id),
      fullproduct: item?.fullproduct || null,
      title,
      quantity,
      unitPrice,
      totalPrice: unitPrice * quantity,
      barcode
    };
  });
};

const calculateOrderTotal = order =>
  normalizeOrderLines(order).reduce((sum, product) => sum + product.totalPrice, 0);

const buildOrdersUrl = (endpoint, filters = {}) => {
  const { storeId: storeIdParam, orderCode, orderNumber } = filters;
  const hasStoreId = storeIdParam && storeIdParam !== 'undefined' && storeIdParam !== 'null';

  const params = new URLSearchParams();
  if (hasStoreId) {
    params.set('storeidRaw', storeIdParam);
  }
  if (orderCode) {
    params.set('orderCode', orderCode);
  }
  if (orderNumber) {
    params.set('orderNumber', orderNumber);
  }

  let url = `${process.env.REACT_APP_PEGASUS_API_URL}/carrefour/orders/${endpoint}`;
  if (endpoint.startsWith('range/') && hasStoreId) {
    url += `/${encodeURIComponent(storeIdParam)}`;
  }

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
};

// Aggregate orders by date
const aggregateOrdersByDate = (orders) => {
  const dateMap = {};
  
  orders.forEach(order => {
    const orderDate = order.orderDate; // Format: "DD/MM/YYYY"
    if (!orderDate) return;
    
    const total = parseNumeric(order.total || order.totalPrice || 0);
    
    if (!dateMap[orderDate]) {
      dateMap[orderDate] = 0;
    }
    dateMap[orderDate] += total;
  });
  
  return dateMap;
};

// Aggregate orders by user and date
const aggregateOrdersByUserAndDate = (orders, userMap) => {
  const userDateMap = {};
  
  orders.forEach(order => {
    const orderDate = order.orderDate;
    if (!orderDate) return;
    
    const userId = order?.requestContext?.user_id || order?.user_id || 'Unknown';
    const userName = userMap[userId] || userId || 'Unknown';
    const total = parseNumeric(order.total || order.totalPrice || 0);
    
    if (!userDateMap[userName]) {
      userDateMap[userName] = {};
    }
    
    if (!userDateMap[userName][orderDate]) {
      userDateMap[userName][orderDate] = 0;
    }
    
    userDateMap[userName][orderDate] += total;
  });
  
  return userDateMap;
};

// Convert DD/MM/YYYY to Date for sorting
const parseDDMMYYYY = (dateStr) => {
  const [day, month, year] = dateStr.split('/');
  return new Date(year, month - 1, day);
};

// Generate all dates in range
const generateDateRange = (startDate, endDate) => {
  const dates = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  
  while (current <= end) {
    const day = String(current.getDate()).padStart(2, '0');
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const year = current.getFullYear();
    dates.push(`${day}/${month}/${year}`);
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
};

const escapeHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatMoney = value => `${parseNumeric(value).toFixed(2)} DHS`;

const buildCarrefourReportPdfHtml = ({
  reportName,
  dateStart,
  dateEnd,
  generatedAt,
  totals,
  lines,
}) => {
  const generatedLabel = generatedAt ? new Date(generatedAt).toLocaleString('fr-FR') : new Date().toLocaleString('fr-FR');
  const rowsHtml = (Array.isArray(lines) ? lines : [])
    .map(line => `
      <tr>
        <td>${escapeHtml(line?.name || 'Produit')}</td>
        <td>${escapeHtml(line?.productId || line?.product_id || '-')}</td>
        <td class="number">${escapeHtml(line?.quantity ?? 0)}</td>
        <td class="number">${escapeHtml(formatMoney(line?.purchasePrice ?? line?.purchase_price ?? 0))}</td>
        <td class="number">${escapeHtml(formatMoney(line?.averageSellPrice ?? line?.average_sell_price ?? 0))}</td>
        <td class="number">${escapeHtml(formatMoney(line?.totalSell ?? line?.total_sell ?? 0))}</td>
        <td class="number">${escapeHtml(formatMoney(line?.totalPurchase ?? line?.total_purchase ?? 0))}</td>
        <td class="number">${escapeHtml(formatMoney(line?.margin ?? 0))}</td>
      </tr>
    `)
    .join('');

  return `<!doctype html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(reportName || 'Rapport Carrefour')}</title>
  <style>
    body{font-family:Arial,sans-serif;color:#1f2937;margin:24px}
    h1{font-size:22px;margin:0 0 8px}
    .meta{margin:0 0 20px;font-size:13px;color:#4b5563}
    .summary{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:18px}
    .summary-card{border:1px solid #d1d5db;border-radius:8px;padding:10px 14px;min-width:160px}
    .summary-card .label{font-size:12px;color:#6b7280;text-transform:uppercase}
    .summary-card .value{font-size:18px;font-weight:700;margin-top:4px}
    table{width:100%;border-collapse:collapse;font-size:12px}
    th,td{border:1px solid #d1d5db;padding:8px;vertical-align:middle}
    th{background:#f3f4f6;text-align:left}
    .number{text-align:right;white-space:nowrap}
    @media print{
      body{margin:12mm}
      .summary{gap:8px}
      .summary-card{break-inside:avoid}
      tr{break-inside:avoid}
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(reportName || 'Rapport Carrefour')}</h1>
  <div class="meta">
    <div>Période: ${escapeHtml(dateStart || '-')} au ${escapeHtml(dateEnd || '-')}</div>
    <div>Généré le: ${escapeHtml(generatedLabel)}</div>
  </div>
  <div class="summary">
    <div class="summary-card">
      <div class="label">Produits distincts</div>
      <div class="value">${escapeHtml(Array.isArray(lines) ? lines.length : 0)}</div>
    </div>
    <div class="summary-card">
      <div class="label">Qté vendue</div>
      <div class="value">${escapeHtml(totals?.quantity ?? 0)}</div>
    </div>
    <div class="summary-card">
      <div class="label">Total vente</div>
      <div class="value">${escapeHtml(formatMoney(totals?.totalSell ?? totals?.total_sell ?? 0))}</div>
    </div>
    <div class="summary-card">
      <div class="label">Marge</div>
      <div class="value">${escapeHtml(formatMoney(totals?.margin ?? 0))}</div>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Produit</th>
        <th>Product ID</th>
        <th>Quantité</th>
        <th>Prix achat</th>
        <th>Prix vente moyen</th>
        <th>Total vente</th>
        <th>Total achat</th>
        <th>Marge</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml || '<tr><td colspan="8" style="text-align:center;color:#6b7280">Aucune donnée analysée.</td></tr>'}
    </tbody>
  </table>
  <script>
    window.onload = function () {
      setTimeout(function () {
        window.print();
      }, 250);
    };
  </script>
</body>
</html>`;
};

const CarrefourOrdersToday = () => {
  const [orders, setOrders] = useState([]);
  const [stores, setStores] = useState([]);
  const [filterStore, setFilterStore] = useState('');
  const [filterProductType, setFilterProductType] = useState('');
  const [filterOrderCode, setFilterOrderCode] = useState('');
  const [filterOrderNumber, setFilterOrderNumber] = useState('');
  const [productMap, setProductMap] = useState({});
  const [productDetailsMap, setProductDetailsMap] = useState({});
  const [carrefourPluCategories, setCarrefourPluCategories] = useState([]);
  const [userList, setUserList] = useState([]);
  const [userPickerOrder, setUserPickerOrder] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isUpdatingOrderUser, setIsUpdatingOrderUser] = useState(false);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [displayDateLabel, setDisplayDateLabel] = useState('');
  const [openRows, setOpenRows] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showCharts, setShowCharts] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [analysisStart, setAnalysisStart] = useState('');
  const [analysisEnd, setAnalysisEnd] = useState('');
  const [analysisStore, setAnalysisStore] = useState('');
  const [analysisProductType, setAnalysisProductType] = useState('');
  const [analysisOrders, setAnalysisOrders] = useState([]);
  const [analysisSeedLines, setAnalysisSeedLines] = useState([]);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [purchasePrices, setPurchasePrices] = useState({});
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportName, setReportName] = useState('');
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);
  const [reportHistoryLoading, setReportHistoryLoading] = useState(false);
  const [editingReportId, setEditingReportId] = useState(null);
  const { date: routeDate, date1: routeDateStart, date2: routeDateEnd } = useParams();
  const navigate = useNavigate();
  const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
  const defaultStoreId = sessionStorage.getItem('default_store_id');
  const isCarrefour = roles.includes('carrefour');
  const isOperator = roles.includes('operator') || roles.includes('driver');
  const isAdmin = roles.includes('admin');
  const isManager = roles.includes('manager');
  const isRestrictedCarrefour = isCarrefour && !isAdmin && !isManager;
  const canDeleteOrder = !isRestrictedCarrefour;
  const allowed = isCarrefour || isManager || isAdmin || isOperator;
  const canAccessAnalysis = isAdmin || isManager;
  const fetchCarrefourPluCategories = async () => {
    try {
      const variables = await VariableService.listVariables('plu');
      const list = Array.isArray(variables)
        ? variables
        : Array.isArray(variables?.data)
        ? variables.data
        : [];
      const pluVariable = list.find((entry) =>
        String(entry?.name || '').trim().toLowerCase().includes('plu carrefour')
      );
      const parsedCategories = pluVariable?.value
        ? JSON.parse(pluVariable.value)
        : [];

      return Array.isArray(parsedCategories) ? parsedCategories : [];
    } catch (error) {
      return [];
    }
  };

  const [appliedFilters, setAppliedFilters] = useState(() =>
    isRestrictedCarrefour && defaultStoreId ? { storeId: defaultStoreId } : {}
  );

  const defaultTag = 7;
  const defaultWarehouse = sessionStorage.getItem('warehouseId') || 3;
  const updateRangeDisplay = useCallback((startDateObj, endDateObj = null) => {
    if (!startDateObj) {
      setDisplayDateLabel('');
      return;
    }
    setRangeStart(toIsoDate(startDateObj));
    setRangeEnd(toIsoDate(endDateObj || startDateObj));
    setDisplayDateLabel(formatDateLabel(startDateObj, endDateObj));
  }, []);

  const loadOrders = useCallback(
    (startIso = 'today', endIso = null, options = {}) => {
      const endpoint = resolveOrdersEndpoint(startIso, endIso);
      const url = buildOrdersUrl(endpoint, options);

      axios
        .get(url, header())
        .then(res => {
          let list = res.data || [];
          if (isRestrictedCarrefour && defaultStoreId) {
            list = list.filter(o => String(o.store_id) === String(defaultStoreId));
          }
          setOrders(list);
        })
        .catch(() => setOrders([]));
  },
  [defaultStoreId, isRestrictedCarrefour]
);

  const effectiveFilters = useMemo(() => {
    const base = appliedFilters || {};
    if (isRestrictedCarrefour && defaultStoreId && !base.storeId) {
      return { ...base, storeId: defaultStoreId };
    }
    return base;
  }, [appliedFilters, defaultStoreId, isRestrictedCarrefour]);


  useEffect(() => {
    (async () => {
      try {
        const list = await StoresHelper();
        if (isRestrictedCarrefour && defaultStoreId) {
          const st = list.find(s => String(s.id) === String(defaultStoreId));
          setStores(st ? [st] : []);
          setFilterStore(defaultStoreId);
          setAppliedFilters(prev => {
            if (prev && String(prev.storeId) === String(defaultStoreId)) {
              return prev;
            }
            return { ...prev, storeId: defaultStoreId };
          });
        } else {
          setStores(list);
        }
      } catch (e) {
        setStores([]);
      }
    })();
  }, [isRestrictedCarrefour, defaultStoreId]);

  useEffect(() => {
    if (!isAdmin && !isManager) {
      setUserList([]);
      return;
    }

    let cancelled = false;

    const loadUsers = async () => {
      try {
        const data = await fetchUsers();
        if (cancelled) return;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.users)
          ? data.users
          : [];
        setUserList(list);
      } catch (error) {
        if (!cancelled) {
          setUserList([]);
        }
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, isManager]);

  useEffect(() => {
    const fetchProducts = async () => {
      const token = sessionStorage.getItem('token');
      const selectedTag = isCarrefour ? defaultTag : null;
      const selectedDefaultWarehouse = isCarrefour ? defaultWarehouse : null;
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_PEGASUS_API_URL}/dolibarr/${selectedTag || 7}/products`,
          { dolapikey: token },
          header()
        );
        const list = response.data || [];
        const map = {};
        (list || []).forEach(p => {
          const key = (p.name || p.label || '').toLowerCase();
          if (key) map[key] = p.barcode;
        });
        setProductMap(map);
      } catch (e) {
        setProductMap({});
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const list = await ProductService.fetchProducts();
        const map = (Array.isArray(list) ? list : []).reduce((acc, product) => {
          addProductLookupKey(acc, product?._id, product);
          addProductLookupKey(acc, product?.id, product);
          addProductLookupKey(acc, product?.dolibarr_ref, product);
          addProductLookupKey(acc, product?.sku, product);
          addProductLookupKey(acc, product?.barcode, product);

          if (Array.isArray(product?.details)) {
            product.details.forEach((detail) => {
              addProductLookupKey(acc, detail?.intern_code, product);
            });
          }

          return acc;
        }, {});
        setProductDetailsMap(map);
      } catch (error) {
        setProductDetailsMap({});
      }
    };

    fetchAllProducts();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCarrefourPluCategories = async () => {
      const categories = await fetchCarrefourPluCategories();

      if (!cancelled) {
        setCarrefourPluCategories(categories);
      }
    };

    loadCarrefourPluCategories();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadReportHistory = useCallback(async () => {
    setReportHistoryLoading(true);
    try {
      const response = await VariableService.listVariables('carrefour_report');
      const list = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
      setReportHistory(list);
    } catch (error) {
      setReportHistory([]);
    } finally {
      setReportHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccessAnalysis && activeTab === 'analysis') {
      setActiveTab('orders');
    }
  }, [activeTab, canAccessAnalysis]);

  useEffect(() => {
    if (activeTab === 'analysis') {
      loadReportHistory();
    }
  }, [activeTab, loadReportHistory]);

  useEffect(() => {
    if (!allowed) return;
    const maxRangeDays = isRestrictedCarrefour ? MAX_RANGE_DAYS_RESTRICTED : undefined;

    if (routeDateStart && routeDateEnd) {
      const rangeResult = parseRangeValues(routeDateStart, routeDateEnd, maxRangeDays);
      if (!rangeResult.valid) {
        if (rangeResult.reason === 'order') {
          const startDate = parseDateInput(routeDateEnd);
          const endDate = parseDateInput(routeDateStart);
          if (startDate && endDate) {
            const isoStart = toIsoDate(startDate);
            const isoEnd = toIsoDate(endDate);
            navigate(`/carrefour/orders/range/${isoStart}/${isoEnd}`, { replace: true });
            return;
          }
        }
        if (rangeResult.reason === 'rangeLimit' && rangeResult.startDate) {
          const limitedEnd = new Date(
            rangeResult.startDate.getTime() + MAX_RANGE_DAYS_RESTRICTED * 24 * 60 * 60 * 1000
          );
          const isoStart = toIsoDate(rangeResult.startDate);
          const isoEnd = toIsoDate(limitedEnd);
          if (isRestrictedCarrefour) {
            window.alert('La recherche est limitée à une période maximale de 7 jours.');
          }
          navigate(`/carrefour/orders/range/${isoStart}/${isoEnd}`, { replace: true });
          return;
        }
        navigate('/carrefour/orders/today');
        return;
      }
      const { startDate, endDate } = rangeResult;
      updateRangeDisplay(startDate, endDate);
      loadOrders(toIsoDate(startDate), toIsoDate(endDate), effectiveFilters);

      return;
    }

    if (routeDate) {
      const parsed = parseDateInput(routeDate);
      if (!parsed) {
        navigate('/carrefour/orders/today');
        return;
      }
      updateRangeDisplay(parsed);
      loadOrders(toIsoDate(parsed), null, effectiveFilters);
      return;
    }

    const today = new Date();
    updateRangeDisplay(today);
    loadOrders('today', null, effectiveFilters);
  }, [
    allowed,
    isRestrictedCarrefour,
    loadOrders,
    navigate,
    routeDate,
    routeDateEnd,
    routeDateStart,
    updateRangeDisplay,
    effectiveFilters
  ]);

  const toggleRow = id => {
    setOpenRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePrintTicket = order => {
    const normalizedLines = normalizeOrderLines(order);
    const directPluLine = normalizedLines.find((line) =>
      resolveStoredPlu(
        line.fullproduct || productDetailsMap[normalizeId(line.productId)] || null
      )
    );
    let resolvedPluCategories = carrefourPluCategories;
    const finalizePrint = (categories) => {
      let firstResolvedPlu = '';
      let firstResolvedPluLabel = '';
      const lines = normalizedLines.map(line => {
        const key = (line.title || '').toLowerCase();
        const details =
          line.fullproduct ||
          productDetailsMap[normalizeId(line.productId)] ||
          null;
        const plu = resolveStoredPlu(details, categories);
        if (!firstResolvedPlu && plu) {
          firstResolvedPlu = plu;
          firstResolvedPluLabel = line.title || 'PLU Carrefour';
        }
        const barcode = line.barcode || productMap[key] || '';
        const price = Number((line.unitPrice * line.quantity).toFixed(2));
        return {
          description: line.title,
          quantity: line.quantity,
          price,
          barcodeSvg: barcode ? generateBarcodeFromValue(barcode) : '',
          barcode
        };
      });
      const total = lines.reduce((sum, l) => sum + l.price, 0);
      const orderDateTime = formatOrderTicketDateTime(
        order?.created_at || order?.createdAt || new Date()
      );

      const pluTicket = buildCarrefourPluTicket({
        plu: firstResolvedPlu,
        total,
        label: firstResolvedPluLabel,
        description: `Commande Carrefour - ${firstResolvedPluLabel || 'PLU'}`,
      });

      localStorage.setItem('orderTicket', JSON.stringify({
        storeName: storeMap[order.store_id] || order.store_id,
        lines,
        total,
        orderDateTime,
        pluTicket: pluTicket || undefined,
        ticketType: pluTicket ? 'carrefour-plu' : 'default',
      }));
      window.open('/order-ticket', '_blank');
    };

    if (!directPluLine && !resolvedPluCategories.length) {
      fetchCarrefourPluCategories().then((categories) => {
        if (categories.length) {
          setCarrefourPluCategories(categories);
          resolvedPluCategories = categories;
        }
        finalizePrint(resolvedPluCategories);
      });
      return;
    }

    finalizePrint(resolvedPluCategories);
  };

  const handleRangeFilter = useCallback(() => {
    const rangeResult = parseRangeValues(
      rangeStart,
      rangeEnd,
      isRestrictedCarrefour ? MAX_RANGE_DAYS_RESTRICTED : undefined
    );
    if (!rangeResult.valid) {
      const message =
        rangeResult.reason === 'order'
          ? 'La date de début doit être antérieure ou égale à la date de fin.'
          : rangeResult.reason === 'rangeLimit'
          ? 'La plage de dates ne peut pas dépasser 7 jours.'
          : 'Veuillez sélectionner une date de début et une date de fin valides.';
      window.alert(message);
      return;
    }
    const { startDate, endDate } = rangeResult;
    const isoStart = toIsoDate(startDate);
    const isoEnd = toIsoDate(endDate);
    if (isoStart === isoEnd) {
      navigate(`/carrefour/orders/${isoStart}`);
    } else {
      navigate(`/carrefour/orders/range/${isoStart}/${isoEnd}`);
    }
  }, [isRestrictedCarrefour, navigate, rangeEnd, rangeStart]);

  const handleReload = useCallback(() => {
    let startValue = rangeStart || routeDateStart || routeDate;
    let endValue = rangeEnd || routeDateEnd || rangeStart || routeDate;

    const buildOptions = () => {
      const opts = {};
      const storeCandidate = filterStore || (isRestrictedCarrefour ? defaultStoreId : '');
      if (storeCandidate) {
        opts.storeId = storeCandidate;
      }
      if (filterOrderCode.trim()) {
        opts.orderCode = filterOrderCode.trim();
      }
      if (filterOrderNumber.trim()) {
        opts.orderNumber = filterOrderNumber.trim();
      }
      return opts;
    };

    if (!startValue) {
      const options = buildOptions();
      setAppliedFilters(options);
      loadOrders('today', null, options);
      return;
    }

    if (!endValue) {
      endValue = startValue;
    }

    const rangeResult = parseRangeValues(
      startValue,
      endValue,
      isRestrictedCarrefour ? MAX_RANGE_DAYS_RESTRICTED : undefined
    );
    if (!rangeResult.valid) {
      const message =
        rangeResult.reason === 'order'
          ? 'La date de début doit être antérieure ou égale à la date de fin.'
          : rangeResult.reason === 'rangeLimit'
          ? 'La plage de dates ne peut pas dépasser 7 jours.'
          : 'Veuillez sélectionner une plage de dates valide avant de recharger.';
      window.alert(message);
      return;
    }

    const { startDate, endDate } = rangeResult;
    updateRangeDisplay(startDate, endDate);
    const options = buildOptions();
    setAppliedFilters(options);
    loadOrders(toIsoDate(startDate), toIsoDate(endDate), options);
  }, [
    loadOrders,
    rangeEnd,
    rangeStart,
    isRestrictedCarrefour,
    routeDate,
    routeDateEnd,
    routeDateStart,
    updateRangeDisplay,
    filterStore,
    defaultStoreId,
    filterOrderCode,
    filterOrderNumber,
    setAppliedFilters
  ]);

  const handleDeleteOrder = async order => {
    if (!order || !order._id) return;
    const confirmDelete = window.confirm('Supprimer cette commande Carrefour ?');
    if (!confirmDelete) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_PEGASUS_API_URL}/carrefour/${order._id}`,
        header()
      );
      if (response?.data?.success) {
        setOrders(prev => prev.filter(o => o._id !== order._id));
      } else {
        const message = response?.data?.message || "Impossible de supprimer la commande.";
        window.alert(message);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || 'Une erreur est survenue lors de la suppression.';
      window.alert(message);
    }
  };

  const storeMap = useMemo(
    () =>
      stores.reduce((acc, s) => {
        acc[s.id] = s.label;
        return acc;
      }, {}),
    [stores]
  );

  const userMap = useMemo(
    () =>
      userList.reduce((acc, user) => {
        const parts = [user?.name, user?.lastname].filter(Boolean);
        const fallback = user?.username || user?.email || user?.phone || user?._id || '';
        const label = parts.join(' ').trim() || fallback;
        if (user?._id) {
          acc[user._id] = label;
        }
        return acc;
      }, {}),
    [userList]
  );

  const visibleOrders = useMemo(() => {
    if (!isCarrefour) {
      return orders;
    }

    return orders.filter(order => {
      const productType = String(order?.productType || '').toLowerCase();
      return productType !== 'glovo';
    });
  }, [isCarrefour, orders]);

  const availableProductTypes = useMemo(() => {
    const unique = new Set();
    visibleOrders.forEach(order => {
      if (order?.productType) {
        unique.add(order.productType);
      }
    });
    return Array.from(unique).sort((a, b) => String(a).localeCompare(String(b)));
  }, [visibleOrders]);

  const filteredOrders = useMemo(
    () =>
      visibleOrders.filter(order => {
        if (filterStore && String(order.store_id) !== String(filterStore)) {
          return false;
        }
        if (filterProductType && String(order.productType || '') !== String(filterProductType)) {
          return false;
        }
        const orderCodeValue = String(order.orderCode || '').toLowerCase();
        const orderNumberValue = String(order.orderNumber || '').toLowerCase();
        if (filterOrderCode.trim()) {
          const target = filterOrderCode.trim().toLowerCase();
          const matches = [orderCodeValue, orderNumberValue].some(value => value.includes(target));
          if (!matches) {
            return false;
          }
        }
        if (filterOrderNumber.trim()) {
          const target = filterOrderNumber.trim().toLowerCase();
          const matches = [orderNumberValue, orderCodeValue].some(value => value.includes(target));
          if (!matches) {
            return false;
          }
        }
        return true;
      }),
    [filterOrderCode, filterOrderNumber, filterProductType, filterStore, visibleOrders]
  );

  useEffect(() => {
    setPage(0);
  }, [filteredOrders.length, rowsPerPage]);

  const totalsByPlatform = useMemo(() => {
    const platformTotals = {};
    filteredOrders.forEach(order => {
      const platform = order?.productType || 'Autre';
      const current = platformTotals[platform] || 0;
      platformTotals[platform] = current + calculateOrderTotal(order);
    });
    return platformTotals;
  }, [filteredOrders]);

  const grandTotal = useMemo(
    () => filteredOrders.reduce((sum, order) => sum + calculateOrderTotal(order), 0),
    [filteredOrders]
  );

  const averageBasketByProductType = useMemo(() => {
    const stats = {};

    filteredOrders.forEach(order => {
      const productType = order?.productType || 'Autre';
      const total = calculateOrderTotal(order);

      if (!stats[productType]) {
        stats[productType] = { total: 0, count: 0 };
      }

      stats[productType].total += total;
      stats[productType].count += 1;
    });

    return Object.entries(stats).reduce((acc, [productType, value]) => {
      acc[productType] = value.count > 0 ? value.total / value.count : 0;
      return acc;
    }, {});
  }, [filteredOrders]);

  const averageBasketAllProducts = useMemo(() => {
    if (!filteredOrders.length) return 0;
    return grandTotal / filteredOrders.length;
  }, [filteredOrders, grandTotal]);

  const analysisSummary = useMemo(() => {
    const grouped = {};

    if (analysisSeedLines.length > 0) {
      analysisSeedLines.forEach(item => {
        const productId = normalizeId(item?.productId || item?.product_id);
        if (!productId) return;

        grouped[productId] = {
          productId,
          name: item?.name || 'Produit',
          imageUrl: item?.imageUrl || '',
          quantity: Number(item?.quantity) || 0,
          totalSell: Number(item?.totalSell ?? item?.total_sell) || 0,
          orderCount: Number(item?.orderCount) || 0,
        };
      });
    } else {
      analysisOrders.forEach(order => {
        const soldProducts = Array.isArray(order?.products) ? order.products : [];
        soldProducts.forEach(item => {
          const productId = normalizeId(item?.id_product || item?.product_id || item?.fk_product);
          if (!productId) return;

          const details = productDetailsMap[normalizeId(productId)] || null;
          const quantityRaw = item?.quantity ?? item?.qty ?? 0;
          const quantityParsed = parseInt(quantityRaw, 10);
          const quantity = Number.isFinite(quantityParsed) && quantityParsed > 0 ? quantityParsed : 0;
          const unitPrice = parseNumeric(
            item?.price ?? details?.regular_price ?? details?.price ?? details?.prices?.price ?? 0
          );
          const totalSell = unitPrice * quantity;

          if (!grouped[productId]) {
            grouped[productId] = {
              productId,
              name: details?.name || details?.label || item?.title || item?.name || 'Produit',
              imageUrl: resolveImageUrl(details) || resolveImageUrl(item),
              quantity: 0,
              totalSell: 0,
              orderCount: 0,
            };
          }

          grouped[productId].quantity += quantity;
          grouped[productId].totalSell += totalSell;
          grouped[productId].orderCount += 1;
        });
      });
    }

    return Object.values(grouped)
      .map(item => {
        const averageSellPrice = item.quantity > 0 ? item.totalSell / item.quantity : 0;
        const purchasePrice = Number(purchasePrices[item.productId] || 0);
        const totalPurchase = purchasePrice * item.quantity;
        const margin = item.totalSell - totalPurchase;

        return {
          ...item,
          purchasePrice,
          averageSellPrice,
          totalPurchase,
          margin,
        };
      })
      .sort((a, b) => b.totalSell - a.totalSell);
  }, [analysisOrders, analysisSeedLines, productDetailsMap, purchasePrices]);

  const analysisGrandTotals = useMemo(() => {
    return analysisSummary.reduce(
      (acc, item) => {
        acc.quantity += item.quantity;
        acc.totalSell += item.totalSell;
        acc.totalPurchase += item.totalPurchase;
        acc.margin += item.margin;
        return acc;
      },
      { quantity: 0, totalSell: 0, totalPurchase: 0, margin: 0 }
    );
  }, [analysisSummary]);

  const handleAnalysisRange = async () => {
    const maxRangeDays = isRestrictedCarrefour ? MAX_RANGE_DAYS_RESTRICTED : undefined;
    const rangeResult = parseRangeValues(analysisStart, analysisEnd, maxRangeDays);

    if (!rangeResult.valid) {
      if (rangeResult.reason === 'rangeLimit') {
        window.alert('La recherche est limitée à une période maximale de 7 jours.');
      } else {
        window.alert('Veuillez saisir une période valide.');
      }
      return;
    }

    setAnalysisLoading(true);
    try {
      const startIso = toIsoDate(rangeResult.startDate);
      const endIso = toIsoDate(rangeResult.endDate);
      const endpoint = resolveOrdersEndpoint(startIso, endIso);
      const analysisFilters = {
        ...effectiveFilters,
        ...(analysisStore ? { storeId: analysisStore } : {}),
      };
      const url = buildOrdersUrl(endpoint, analysisFilters);
      const res = await axios.get(url, header());
      let list = res.data || [];
      if (isRestrictedCarrefour && defaultStoreId) {
        list = list.filter(o => String(o.store_id) === String(defaultStoreId));
      }
      if (analysisProductType) {
        list = list.filter(o => String(o?.productType || '') === String(analysisProductType));
      }
      setAnalysisOrders(list);
      setAnalysisSeedLines([]);
      setPurchasePrices({});
      setReportName(`carrefour_report_${startIso}_${endIso}`);
      setEditingReportId(null);
    } catch (error) {
      setAnalysisOrders([]);
      setAnalysisSeedLines([]);
      toast.error("Erreur lors du chargement de l'analyse Carrefour");
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handlePurchasePriceChange = (productId, value) => {
    setPurchasePrices(prev => ({
      ...prev,
      [productId]: value
    }));
  };

  const exportAnalysisReportPdf = useCallback((reportOverride = null) => {
    const sourceLines = reportOverride?.lines || analysisSummary;
    const sourceTotals = reportOverride?.totals || analysisGrandTotals;
    const sourceName = reportOverride?.reportName || reportName || `carrefour_report_${analysisStart}_${analysisEnd}`;
    const sourceStart = reportOverride?.dateStart || analysisStart;
    const sourceEnd = reportOverride?.dateEnd || analysisEnd;
    const sourceGeneratedAt = reportOverride?.generatedAt || new Date().toISOString();

    if (!Array.isArray(sourceLines) || sourceLines.length === 0) {
      toast.error('Aucune donnée à exporter en PDF.');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1200,height=900');
    if (!printWindow) {
      toast.error('Veuillez autoriser les popups pour générer le PDF.');
      return;
    }

    const html = buildCarrefourReportPdfHtml({
      reportName: sourceName,
      dateStart: sourceStart,
      dateEnd: sourceEnd,
      generatedAt: sourceGeneratedAt,
      totals: sourceTotals,
      lines: sourceLines,
    });

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }, [analysisEnd, analysisGrandTotals, analysisStart, analysisSummary, reportName]);

  const openSavedReport = report => {
    let parsedValue = {};
    try {
      parsedValue = JSON.parse(report?.value || '{}');
    } catch (error) {
      toast.error('Impossible de lire ce rapport.');
      return;
    }

    const lines = Array.isArray(parsedValue?.lines) ? parsedValue.lines : [];
    const restoredPurchasePrices = lines.reduce((acc, line) => {
      const productId = normalizeId(line?.product_id);
      if (productId) {
        acc[productId] = line?.purchase_price ?? '';
      }
      return acc;
    }, {});

    setAnalysisStart(parsedValue?.date_start || '');
    setAnalysisEnd(parsedValue?.date_end || '');
    setAnalysisStore(parsedValue?.filters?.storeId || '');
    setAnalysisProductType(parsedValue?.filters?.productType || '');
    setAnalysisOrders([]);
    setAnalysisSeedLines(
      lines.map(line => {
        const details = productDetailsMap[normalizeId(line?.product_id)] || null;
        return {
          productId: normalizeId(line?.product_id),
          name: line?.name || details?.name || details?.label || 'Produit',
          imageUrl: line?.image_url || resolveImageUrl(details) || '',
          quantity: Number(line?.quantity) || 0,
          totalSell: Number(line?.total_sell) || 0,
          orderCount: 0,
        };
      })
    );
    setPurchasePrices(restoredPurchasePrices);
    setReportName(report?.name || '');
    setEditingReportId(report?._id || report?.id || null);
  };

  const exportSavedReportPdf = report => {
    let parsedValue = {};
    try {
      parsedValue = JSON.parse(report?.value || '{}');
    } catch (error) {
      toast.error('Impossible de lire ce rapport.');
      return;
    }

    const lines = Array.isArray(parsedValue?.lines) ? parsedValue.lines : [];
    if (!lines.length) {
      toast.error('Ce rapport ne contient aucune ligne à exporter.');
      return;
    }

    exportAnalysisReportPdf({
      reportName: report?.name || 'Rapport Carrefour',
      dateStart: parsedValue?.date_start || '',
      dateEnd: parsedValue?.date_end || '',
      generatedAt: parsedValue?.generated_at || report?.createdAt || report?.updatedAt || new Date().toISOString(),
      totals: parsedValue?.totals || {},
      lines,
    });
  };

  const handleSaveReport = async () => {
    if (!analysisStart || !analysisEnd) {
      window.alert('Veuillez choisir une période.');
      return;
    }

    const payload = {
      date_start: analysisStart,
      date_end: analysisEnd,
      generated_at: new Date().toISOString(),
      filters: {
        ...effectiveFilters,
        ...(analysisStore ? { storeId: analysisStore } : {}),
        ...(analysisProductType ? { productType: analysisProductType } : {}),
      },
      totals: analysisGrandTotals,
      lines: analysisSummary.map(item => ({
        product_id: item.productId,
        name: item.name,
        image_url: item.imageUrl || '',
        quantity: item.quantity,
        average_sell_price: item.averageSellPrice,
        total_sell: item.totalSell,
        purchase_price: item.purchasePrice,
        total_purchase: item.totalPurchase,
        margin: item.margin,
      }))
    };

    const data = new FormData();
    data.append('name', reportName || `carrefour_report_${analysisStart}_${analysisEnd}`);
    data.append('value', JSON.stringify(payload));
    data.append('type', 'carrefour_report');

    setIsSavingReport(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_PEGASUS_API_URL}/variables`,
        data,
        header({ 'Content-Type': 'multipart/form-data' })
      );
      toast.success('Rapport Carrefour enregistré avec succès');
      setReportModalOpen(false);
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du rapport");
    } finally {
      setIsSavingReport(false);
    }
  };

  const handlePersistReport = async () => {
    if (!analysisStart || !analysisEnd) {
      window.alert('Veuillez choisir une pÃ©riode.');
      return;
    }

    const payload = {
      date_start: analysisStart,
      date_end: analysisEnd,
      generated_at: new Date().toISOString(),
      filters: {
        ...effectiveFilters,
        ...(analysisStore ? { storeId: analysisStore } : {}),
        ...(analysisProductType ? { productType: analysisProductType } : {}),
      },
      totals: analysisGrandTotals,
      lines: analysisSummary.map(item => ({
        product_id: item.productId,
        name: item.name,
        image_url: item.imageUrl || '',
        quantity: item.quantity,
        average_sell_price: item.averageSellPrice,
        total_sell: item.totalSell,
        purchase_price: item.purchasePrice,
        total_purchase: item.totalPurchase,
        margin: item.margin,
      }))
    };

    const data = new FormData();
    data.append('name', reportName || `carrefour_report_${analysisStart}_${analysisEnd}`);
    data.append('value', JSON.stringify(payload));
    data.append('type', 'carrefour_report');

    setIsSavingReport(true);
    try {
      if (editingReportId) {
        await axios.put(
          `${process.env.REACT_APP_PEGASUS_API_URL}/variables/${editingReportId}`,
          data,
          header({ 'Content-Type': 'multipart/form-data' })
        );
        toast.success('Rapport Carrefour mis Ã  jour avec succÃ¨s');
      } else {
        await axios.post(
          `${process.env.REACT_APP_PEGASUS_API_URL}/variables`,
          data,
          header({ 'Content-Type': 'multipart/form-data' })
        );
        toast.success('Rapport Carrefour enregistrÃ© avec succÃ¨s');
      }
      setReportModalOpen(false);
      loadReportHistory();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du rapport");
    } finally {
      setIsSavingReport(false);
    }
  };

  const paginatedOrders = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredOrders.slice(start, start + rowsPerPage);
  }, [filteredOrders, page, rowsPerPage]);

  // Prepare chart data for amount per day
  const chartDataByDate = useMemo(() => {
    if (!filteredOrders.length) return null;

    const dateMap = aggregateOrdersByDate(filteredOrders);
    const sortedDates = Object.keys(dateMap).sort((a, b) => parseDDMMYYYY(a) - parseDDMMYYYY(b));
    
    // If we have a date range, fill in missing dates
    let allDates = sortedDates;
    if (rangeStart && rangeEnd) {
      const startDate = parseDateInput(rangeStart);
      const endDate = parseDateInput(rangeEnd);
      if (startDate && endDate) {
        allDates = generateDateRange(startDate, endDate);
      }
    }
    
    const amounts = allDates.map(date => dateMap[date] || 0);

    return {
      labels: allDates,
      datasets: [
        {
          label: 'Montant (DHS)',
          data: amounts,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1
        }
      ]
    };
  }, [filteredOrders, rangeStart, rangeEnd]);

  // Prepare chart data for amount by user per day
  const chartDataByUserAndDate = useMemo(() => {
    if (!filteredOrders.length) return null;

    const userDateMap = aggregateOrdersByUserAndDate(filteredOrders, userMap);
    const users = Object.keys(userDateMap);
    
    if (!users.length) return null;

    // Get all dates
    const allDatesSet = new Set();
    users.forEach(user => {
      Object.keys(userDateMap[user]).forEach(date => allDatesSet.add(date));
    });
    
    let allDates = Array.from(allDatesSet).sort((a, b) => parseDDMMYYYY(a) - parseDDMMYYYY(b));
    
    // If we have a date range, fill in missing dates
    if (rangeStart && rangeEnd) {
      const startDate = parseDateInput(rangeStart);
      const endDate = parseDateInput(rangeEnd);
      if (startDate && endDate) {
        allDates = generateDateRange(startDate, endDate);
      }
    }

    // Generate colors for each user
    const colors = [
      'rgb(255, 99, 132)',
      'rgb(54, 162, 235)',
      'rgb(255, 206, 86)',
      'rgb(75, 192, 192)',
      'rgb(153, 102, 255)',
      'rgb(255, 159, 64)',
      'rgb(199, 199, 199)',
      'rgb(83, 102, 255)',
      'rgb(255, 99, 255)',
      'rgb(99, 255, 132)'
    ];

    const datasets = users.map((user, index) => {
      const amounts = allDates.map(date => userDateMap[user][date] || 0);
      const color = colors[index % colors.length];
      
      return {
        label: user,
        data: amounts,
        borderColor: color,
        backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.2)'),
        tension: 0.1
      };
    });

    return {
      labels: allDates,
      datasets
    };
  }, [filteredOrders, userMap, rangeStart, rangeEnd]);

  const resolveOrderUserLabel = useCallback(
    order => {
      const requestUserId = order?.requestContext?.user_id || order?.user_id;
      if (!requestUserId) {
        return '—';
      }
      return userMap[requestUserId] || requestUserId;
    },
    [userMap]
  );

  const getProductDetails = useCallback(id => productDetailsMap[normalizeId(id)] || null, [
    productDetailsMap
  ]);

  const buildSoldProductView = useCallback(
    item => {
      const details = getProductDetails(item?.id_product || item?.product_id || item?.fk_product);
      const imageUrl = resolveImageUrl(details) || resolveImageUrl(item);
      const name =
        item?.title ||
        item?.name ||
        item?.product_label ||
        details?.name ||
        details?.label ||
        'Produit';
      const quantityRaw = item?.quantity ?? item?.qty ?? 0;
      const quantityParsed = parseInt(quantityRaw, 10);
      const quantity = Number.isFinite(quantityParsed) && quantityParsed > 0 ? quantityParsed : 0;
      const unitPrice = parseNumeric(
        item?.price ?? details?.regular_price ?? details?.price ?? details?.prices?.price ?? 0
      );

      return {
        imageUrl,
        name,
        quantity,
        unitPrice,
        totalPrice: unitPrice * quantity
      };
    },
    [getProductDetails]
  );

  const buildReferenceProductView = useCallback(
    item => {
      const details = getProductDetails(item?.product_id || item?.fk_product || item?.id_product);
      const imageUrl = resolveImageUrl(details) || resolveImageUrl(item);
      const name = details?.name || details?.label || item?.name || item?.title || 'Référence';
      const quantityRaw = item?.qty ?? item?.quantity ?? 0;
      const quantityParsed = parseInt(quantityRaw, 10);
      const quantity = Number.isFinite(quantityParsed) && quantityParsed > 0 ? quantityParsed : 0;

      return {
        imageUrl,
        name,
        quantity
      };
    },
    [getProductDetails]
  );

  const openUserPicker = order => {
    setUserPickerOrder(order);
    const requestUserId = order?.requestContext?.user_id || order?.user_id || '';
    setSelectedUserId(requestUserId || '');
  };

  const closeUserPicker = () => {
    setUserPickerOrder(null);
    setSelectedUserId('');
  };

  const handleConfirmUserChange = async () => {
    if (!userPickerOrder?._id) {
      return;
    }
    if (!selectedUserId) {
      window.alert('Veuillez sélectionner un utilisateur.');
      return;
    }

    const orderIdRaw = userPickerOrder._id;

    try {
      setIsUpdatingOrderUser(true);
      const response = await updateDolibarrOrderUser(orderIdRaw, selectedUserId);
      const updatedOrder = response?.order;

      setOrders(prev =>
        prev.map(order => {
          if (String(order._id) !== String(orderIdRaw)) {
            return order;
          }

          const nextRequestContext = updatedOrder?.requestContext
            ? updatedOrder.requestContext
            : {
                ...(order.requestContext || {}),
                user_id: selectedUserId
              };

          return {
            ...order,
            ...(updatedOrder || {}),
            requestContext: nextRequestContext
          };
        })
      );

      closeUserPicker();
      window.alert('Utilisateur mis à jour avec succès.');
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Une erreur est survenue lors de la mise à jour de l'utilisateur.";
      window.alert(message);
    } finally {
      setIsUpdatingOrderUser(false);
    }
  };

  if (!allowed) {
    return <div className="alert alert-danger mt-3">Accès non autorisé</div>;
  }

  return (
    <div className="container mt-4">
      <h2 className="mb-3">
        Historique des commandes Carrefour{displayDateLabel ? ` du ${displayDateLabel}` : ''}
      </h2>
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'orders' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveTab('orders')}
          >
            Commandes
          </button>
        </li>
        <li className="nav-item">
          {canAccessAnalysis && (
            <button
              className={`nav-link ${activeTab === 'analysis' ? 'active' : ''}`}
              type="button"
              onClick={() => setActiveTab('analysis')}
            >
              Analyse & rapport
            </button>
          )}
        </li>
      </ul>
      {activeTab === 'orders' || !canAccessAnalysis ? (
        <>
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <h6 className="card-subtitle text-muted mb-2">Total transactions</h6>
              <h4 className="card-title mb-0">{filteredOrders.length}</h4>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <h6 className="card-subtitle text-muted mb-2">Montant total</h6>
              <h4 className="card-title mb-0">{grandTotal.toFixed(2)} DHS</h4>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="card text-center h-100">
            <div className="card-body">
              <h6 className="card-subtitle text-muted mb-2">Panier moyen global</h6>
              <h4 className="card-title mb-0">{averageBasketAllProducts.toFixed(2)} DHS</h4>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-3">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-subtitle text-muted mb-2">Total par plateforme</h6>
              <ul className="list-unstyled mb-0">
                {Object.keys(totalsByPlatform).length === 0 && (
                  <li className="text-muted">Aucune donnée</li>
                )}
                {Object.entries(totalsByPlatform).map(([platform, total]) => (
                  <li key={platform} className="d-flex justify-content-between">
                    <span className="text-capitalize">{platform}</span>
                    <span>{total.toFixed(2)} DHS</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="card h-100">
            <div className="card-body">
              <h6 className="card-subtitle text-muted mb-2">Panier moyen par type de produit</h6>
              <ul className="list-unstyled mb-0">
                {Object.keys(averageBasketByProductType).length === 0 && (
                  <li className="text-muted">Aucune donnée</li>
                )}
                {Object.entries(averageBasketByProductType).map(([productType, average]) => (
                  <li key={productType} className="d-flex justify-content-between">
                    <span className="text-capitalize">{productType}</span>
                    <span>{average.toFixed(2)} DHS</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="row g-2 align-items-end mb-3">
        <div className="col-12 col-sm-auto">
          <button className="btn btn-primary w-100" onClick={handleReload}>
            Recharger
          </button>
        </div>
        <div className="col-12 col-md-3 col-lg-2">
          <label className="form-label mb-1">Date de début</label>
          <input
            type="date"
            className="form-control"
            value={rangeStart}
            onChange={e => setRangeStart(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-3 col-lg-2">
          <label className="form-label mb-1">Date de fin</label>
          <input
            type="date"
            className="form-control"
            value={rangeEnd}
            onChange={e => setRangeEnd(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-3 col-lg-2">
          <label className="form-label mb-1">Code commande</label>
          <input
            type="text"
            className="form-control"
            value={filterOrderCode}
            onChange={e => setFilterOrderCode(e.target.value)}
            placeholder="Code"
          />
        </div>
        <div className="col-12 col-md-3 col-lg-2">
          <label className="form-label mb-1">Numéro de commande</label>
          <input
            type="text"
            className="form-control"
            value={filterOrderNumber}
            onChange={e => setFilterOrderNumber(e.target.value)}
            placeholder="Numéro"
          />
        </div>
        <div className="col-12 col-md-auto">
          <button className="btn btn-outline-secondary w-100" onClick={handleRangeFilter}>
            Filtrer par période
          </button>
        </div>
      </div>
      <select
        className="form-select mb-3"
        value={filterStore}
        onChange={e => setFilterStore(e.target.value)}
        disabled={isRestrictedCarrefour}
      >
        {!isRestrictedCarrefour && <option value="">Toutes les boutiques</option>}
        {stores.map(s => (
          <option key={s.id} value={s.id}>
            {s.label}
          </option>
        ))}
      </select>
      <select
        className="form-select mb-3"
        value={filterProductType}
        onChange={e => setFilterProductType(e.target.value)}
      >
        <option value="">Tous les types de produits</option>
        {availableProductTypes.map(type => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </select>
      <TableContainer component={Paper} className="mb-3">
        <Table>
          <TableHead sx={{ backgroundColor: 'var(--bs-primary)', color: '#fff' }}>
            <TableRow>
              <TableCell />
              <TableCell sx={{ color: '#fff' }}>Date/Time</TableCell>
              <TableCell sx={{ color: '#fff' }}>Store</TableCell>
              <TableCell sx={{ color: '#fff' }}>Order Code</TableCell>
              <TableCell sx={{ color: '#fff' }}>Product Type</TableCell>
              <TableCell sx={{ color: '#fff' }}>Products Count</TableCell>
              <TableCell sx={{ color: '#fff' }}>Total</TableCell>
              <TableCell sx={{ color: '#fff' }}>Vendeur</TableCell>
              <TableCell sx={{ color: '#fff' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrders.map((order, idx) => {
              const absoluteIndex = page * rowsPerPage + idx;
              const rowId = `order-${absoluteIndex}`;
              const soldProducts = Array.isArray(order?.products) ? order.products : [];
              const referenceProducts = Array.isArray(order?.reference_products)
                ? order.reference_products
                : [];

              return (
                <React.Fragment key={rowId}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRow(rowId)}>
                        {openRows[rowId] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{order.orderDate} {order.orderTime}</TableCell>
                    <TableCell>{storeMap[order.store_id] || order.store_id}</TableCell>
                    <TableCell>{order.orderCode || order.orderNumber}</TableCell>
                    <TableCell>{order.productType || '—'}</TableCell>
                    <TableCell>{order.productsCount}</TableCell>
                    <TableCell>{calculateOrderTotal(order).toFixed(2)} DHS</TableCell>
                    <TableCell>{resolveOrderUserLabel(order)}</TableCell>
                    <TableCell>
                      <button className="btn btn-sm btn-primary me-2 m-1" onClick={() => handlePrintTicket(order)}>
                        Ticket
                      </button>
                      {canDeleteOrder && (
                        <button
                          className="btn btn-sm btn-danger me-2 m-1"
                          onClick={() => handleDeleteOrder(order)}
                          disabled={!order._id}
                        >
                          Supprimer
                        </button>
                      )}
                      {(isAdmin || isManager) && (
                        <button
                          className="btn btn-sm btn-outline-primary m-1"
                          onClick={() => openUserPicker(order)}
                          disabled={!userList.length}
                        >
                          Changer vendeur
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                      <Collapse in={!!openRows[rowId]} timeout="auto" unmountOnExit>
                        <Box margin={2}>
                          <Typography variant="subtitle1" gutterBottom>
                            Produits vendus
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Image</TableCell>
                                <TableCell>Produit</TableCell>
                                <TableCell>Quantité</TableCell>
                                <TableCell>Prix unitaire</TableCell>
                                <TableCell>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {soldProducts.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={5} align="center">
                                    Aucun produit
                                  </TableCell>
                                </TableRow>
                              )}
                              {soldProducts.map((item, index) => {
                                const view = buildSoldProductView(item);
                                return (
                                  <TableRow key={index}>
                                    <TableCell>
                                      {view.imageUrl ? (
                                        <Avatar
                                          variant="rounded"
                                          src={view.imageUrl}
                                          alt={view.name}
                                          sx={{ width: 48, height: 48 }}
                                        />
                                      ) : (
                                        <Avatar variant="rounded" sx={{ width: 48, height: 48 }}>
                                          {view.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                      )}
                                    </TableCell>
                                    <TableCell>{view.name}</TableCell>
                                    <TableCell>{view.quantity}</TableCell>
                                    <TableCell>{view.unitPrice.toFixed(2)} DHS</TableCell>
                                    <TableCell>{view.totalPrice.toFixed(2)} DHS</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>

                          <Typography variant="subtitle1" className="mt-3" gutterBottom>
                            Produits référencés
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Image</TableCell>
                                <TableCell>Produit</TableCell>
                                <TableCell>Quantité</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {referenceProducts.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={3} align="center">
                                    Aucun produit référencé
                                  </TableCell>
                                </TableRow>
                              )}
                              {referenceProducts.map((item, index) => {
                                const view = buildReferenceProductView(item);
                                return (
                                  <TableRow key={index}>
                                    <TableCell>
                                      {view.imageUrl ? (
                                        <Avatar
                                          variant="rounded"
                                          src={view.imageUrl}
                                          alt={view.name}
                                          sx={{ width: 48, height: 48 }}
                                        />
                                      ) : (
                                        <Avatar variant="rounded" sx={{ width: 48, height: 48 }}>
                                          {view.name.charAt(0).toUpperCase()}
                                        </Avatar>
                                      )}
                                    </TableCell>
                                    <TableCell>{view.name}</TableCell>
                                    <TableCell>{view.quantity}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={6} align="right">
                Total général :
              </TableCell>
              <TableCell colSpan={3}>{grandTotal.toFixed(2)} DHS</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredOrders.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Lignes par page"
      />

      {/* Charts Section - Collapsible */}
      {(chartDataByDate || chartDataByUserAndDate) && (
        <div className="mb-4">
          <button
            className="btn btn-outline-primary mb-3"
            onClick={() => setShowCharts(!showCharts)}
          >
            {showCharts ? 'Masquer les graphiques' : 'Afficher les graphiques'}
          </button>
          
          <Collapse in={showCharts} timeout="auto">
            <div>
              {chartDataByDate && (
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <h5 className="card-title mb-3">Montant par jour</h5>
                        <Line
                          data={chartDataByDate}
                          options={{
                            responsive: true,
                            plugins: {
                              legend: {
                                position: 'top'
                              },
                              title: {
                                display: true,
                                text: 'Évolution du montant des commandes par jour'
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  callback: function(value) {
                                    return value.toFixed(2) + ' DHS';
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {chartDataByUserAndDate && (
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <h5 className="card-title mb-3">Montant par utilisateur et par jour</h5>
                        <Line
                          data={chartDataByUserAndDate}
                          options={{
                            responsive: true,
                            plugins: {
                              legend: {
                                position: 'top'
                              },
                              title: {
                                display: true,
                                text: 'Évolution du montant des commandes par utilisateur'
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                ticks: {
                                  callback: function(value) {
                                    return value.toFixed(2) + ' DHS';
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Collapse>
        </div>
      )}
        </>
      ) : canAccessAnalysis ? (
        <>
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                  <label className="form-label mb-1">Date de début</label>
                  <input
                    type="date"
                    className="form-control"
                    value={analysisStart}
                    onChange={e => setAnalysisStart(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label mb-1">Date de fin</label>
                  <input
                    type="date"
                    className="form-control"
                    value={analysisEnd}
                    onChange={e => setAnalysisEnd(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label mb-1">Boutique</label>
                  <select
                    className="form-select"
                    value={analysisStore}
                    onChange={e => setAnalysisStore(e.target.value)}
                    disabled={isRestrictedCarrefour}
                  >
                    {!isRestrictedCarrefour && <option value="">Toutes les boutiques</option>}
                    {stores.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label mb-1">Type de produit</label>
                  <select
                    className="form-select"
                    value={analysisProductType}
                    onChange={e => setAnalysisProductType(e.target.value)}
                  >
                    <option value="">Tous les types</option>
                    {availableProductTypes.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-auto">
                  <button className="btn btn-primary w-100" onClick={handleAnalysisRange} disabled={analysisLoading}>
                    {analysisLoading ? 'Analyse...' : 'Analyser'}
                  </button>
                </div>
                <div className="col-12 col-md-auto">
                  <button
                    className="btn btn-outline-success w-100"
                    onClick={() => setReportModalOpen(true)}
                    disabled={!analysisSummary.length}
                  >
                    Créer un rapport
                  </button>
                </div>
                <div className="col-12 col-md-auto">
                  <button
                    className="btn btn-outline-danger w-100"
                    onClick={() => exportAnalysisReportPdf()}
                    disabled={!analysisSummary.length}
                  >
                    Exporter PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-4">
            <div className="col-12 col-md-3">
              <div className="card text-center h-100">
                <div className="card-body">
                  <h6 className="card-subtitle text-muted mb-2">Produits distincts</h6>
                  <h4 className="card-title mb-0">{analysisSummary.length}</h4>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="card text-center h-100">
                <div className="card-body">
                  <h6 className="card-subtitle text-muted mb-2">Qté vendue</h6>
                  <h4 className="card-title mb-0">{analysisGrandTotals.quantity}</h4>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="card text-center h-100">
                <div className="card-body">
                  <h6 className="card-subtitle text-muted mb-2">Total vente</h6>
                  <h4 className="card-title mb-0">{analysisGrandTotals.totalSell.toFixed(2)} DHS</h4>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="card text-center h-100">
                <div className="card-body">
                  <h6 className="card-subtitle text-muted mb-2">Marge</h6>
                  <h4 className="card-title mb-0">{analysisGrandTotals.margin.toFixed(2)} DHS</h4>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Produits vendus</h5>
              <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Image</th>
                      <th>Produit</th>
                      <th>Product ID</th>
                      <th>Quantité</th>
                      <th>Prix achat</th>
                      <th>Prix vente moyen</th>
                      <th>Total vente</th>
                      <th>Total achat</th>
                      <th>Marge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysisSummary.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center text-muted py-4">
                          Aucune donnée analysée.
                        </td>
                      </tr>
                    ) : (
                      analysisSummary.map(item => (
                        <tr key={item.productId}>
                          <td>
                            {item.imageUrl ? (
                              <Avatar variant="rounded" src={item.imageUrl} alt={item.name} sx={{ width: 48, height: 48 }} />
                            ) : (
                              <Avatar variant="rounded" sx={{ width: 48, height: 48 }}>
                                {item.name.charAt(0).toUpperCase()}
                              </Avatar>
                            )}
                          </td>
                          <td>{item.name}</td>
                          <td>{item.productId}</td>
                          <td>{item.quantity}</td>
                          <td style={{ minWidth: 140 }}>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              min="0"
                              step="any"
                              value={purchasePrices[item.productId] ?? ''}
                              onChange={e => handlePurchasePriceChange(item.productId, e.target.value)}
                            />
                          </td>
                          <td>{item.averageSellPrice.toFixed(2)} DHS</td>
                          <td>{item.totalSell.toFixed(2)} DHS</td>
                          <td>{item.totalPurchase.toFixed(2)} DHS</td>
                          <td>{item.margin.toFixed(2)} DHS</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="card mt-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">Historique des rapports</h5>
                <button className="btn btn-outline-secondary btn-sm" onClick={loadReportHistory} disabled={reportHistoryLoading}>
                  {reportHistoryLoading ? 'Chargement...' : 'Actualiser'}
                </button>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Nom</th>
                      <th>Période</th>
                      <th>Créé le</th>
                      <th>Type</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportHistory.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          Aucun rapport enregistré.
                        </td>
                      </tr>
                    ) : (
                      reportHistory.map(report => {
                        let parsedValue = {};
                        try {
                          parsedValue = JSON.parse(report?.value || '{}');
                        } catch (error) {
                          parsedValue = {};
                        }

                        return (
                          <tr key={report?._id || report?.id}>
                            <td>{report?.name || '-'}</td>
                            <td>{parsedValue?.date_start || '—'} au {parsedValue?.date_end || '—'}</td>
                            <td>{report?.createdAt || report?.updatedAt || '—'}</td>
                            <td>{report?.type || 'carrefour_report'}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-primary" onClick={() => openSavedReport(report)}>
                                  Ouvrir
                                </button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => exportSavedReportPdf(report)}>
                                  PDF
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {userPickerOrder && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Changer le vendeur</h5>
                  <button type="button" className="btn-close" onClick={closeUserPicker} aria-label="Close"></button>
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Sélectionnez le nouvel utilisateur associé à la commande <strong>{userPickerOrder.orderCode || userPickerOrder.orderNumber || userPickerOrder._id}</strong>.
                  </p>
                  <select
                    className="form-select"
                    value={selectedUserId}
                    onChange={event => setSelectedUserId(event.target.value)}
                  >
                    <option value="">Sélectionnez un utilisateur</option>
                    {selectedUserId && !userMap[selectedUserId] && (
                      <option value={selectedUserId}>Utilisateur actuel ({selectedUserId})</option>
                    )}
                    {userList.map(user => {
                      const optionLabel = userMap[user?._id] || user?._id;
                      return (
                        <option key={user?._id} value={user?._id}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeUserPicker} disabled={isUpdatingOrderUser}>
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirmUserChange}
                    disabled={isUpdatingOrderUser || !selectedUserId}
                  >
                    {isUpdatingOrderUser ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
      {reportModalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Créer un rapport Carrefour</h5>
                  <button type="button" className="btn-close" onClick={() => setReportModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  {editingReportId && (
                    <div className="alert alert-warning">
                      Vous modifiez un rapport existant.
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Nom de la variable</label>
                    <input
                      type="text"
                      className="form-control"
                      value={reportName}
                      onChange={e => setReportName(e.target.value)}
                    />
                  </div>
                  <div className="alert alert-info mb-0">
                    <div>Période: {analysisStart || '—'} au {analysisEnd || '—'}</div>
                    <div>Produits: {analysisSummary.length}</div>
                    <div>Total vente: {analysisGrandTotals.totalSell.toFixed(2)} DHS</div>
                    <div>Marge: {analysisGrandTotals.margin.toFixed(2)} DHS</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setReportModalOpen(false)}>
                    Fermer
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handlePersistReport} disabled={isSavingReport}>
                    {isSavingReport ? 'Enregistrement...' : editingReportId ? 'Mettre à jour' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default CarrefourOrdersToday;

